import { useCallback, useMemo, useState } from "react";
import { calculationsApi } from "../../../../services/api/calculations";
import { ProjectOverviewItem } from "../../../../types";
import { useSequentiellerLauf } from "../../../../hooks/useSequentiellerLauf";
import { VereinheitlichenWerte } from "../sections/VereinheitlichenDialog";

/** Simulierte Kennzahlen eines Projekts, überlagert die gespeicherten nur in der Anzeige. */
export interface SimulierteKennzahlen {
  irr: number | null;
  wirtschaftlichkeit_score: number | null;
}

export interface SimulationsZustand {
  /** Die gesetzten Werte, solange eine Simulation aktiv ist */
  werte: VereinheitlichenWerte;
  /** Projekt-ID -> simulierte Kennzahlen */
  ergebnisse: Record<string, SimulierteKennzahlen>;
  /** Anzahl der Projekte, die nicht simuliert werden konnten */
  nichtSimulierbar: number;
}

/**
 * Hält die Was-wäre-wenn-Rechnung der Projektübersicht.
 *
 * Bewusst nur im Zustand dieser Seite: Ein Reload oder das Verlassen der Übersicht verwirft
 * die Simulation, wie es die Anzeige auch verspricht. Nichts davon wird gespeichert.
 *
 * Die Projekte werden einzeln durchgerechnet, damit der Fortschritt ehrlich anzeigen kann,
 * welches Projekt gerade dran ist – ein einzelner Bulk-Request könnte das nicht.
 */
export const useWirtschaftlichkeitSimulation = (
  projects: ProjectOverviewItem[],
) => {
  const [simulation, setSimulation] = useState<SimulationsZustand | null>(null);

  const labelVon = useCallback(
    (projekt: ProjectOverviewItem) => projekt.name,
    [],
  );
  const { zustand, starten, abbrechen } = useSequentiellerLauf<
    ProjectOverviewItem,
    SimulierteKennzahlen | null
  >(labelVon);

  const simulieren = useCallback(
    async (werte: VereinheitlichenWerte) => {
      // Eine neue Simulation ersetzt die vorige vollständig – sonst blieben Werte aus einem
      // früheren Lauf für Projekte stehen, die diesmal nicht simulierbar sind.
      setSimulation(null);

      const { erfolge, fehler } = await starten(projects, async (projekt) => {
        const antwort = await calculationsApi.simuliereWirtschaftlichkeit(
          projekt.id,
          werte,
        );
        if (antwort.nicht_simulierbar_grund) return null;
        return {
          irr: antwort.irr,
          wirtschaftlichkeit_score: antwort.wirtschaftlichkeit_score,
        };
      });

      const ergebnisse: Record<string, SimulierteKennzahlen> = {};
      let nichtSimulierbar = fehler.length;

      for (const { element, wert } of erfolge) {
        if (wert === null) {
          nichtSimulierbar++;
        } else {
          ergebnisse[element.id] = wert;
        }
      }

      setSimulation({ werte, ergebnisse, nichtSimulierbar });
    },
    [projects, starten],
  );

  const zuruecksetzen = useCallback(() => setSimulation(null), []);

  /**
   * Die Projektliste mit überlagerten Werten. Projekte ohne Simulationsergebnis behalten
   * ihren gespeicherten Wert und werden nicht als simuliert markiert.
   */
  const angezeigteProjekte = useMemo(() => {
    if (!simulation) return projects;

    return projects.map((projekt) => {
      const simuliert = simulation.ergebnisse[projekt.id];
      if (!simuliert) return projekt;

      return {
        ...projekt,
        irr: simuliert.irr,
        wirtschaftlichkeit_score: simuliert.wirtschaftlichkeit_score,
        zuschlagswert: simulation.werte.zuschlagswert ?? projekt.zuschlagswert,
        kreditzins: simulation.werte.fremdkapitalzins ?? projekt.kreditzins,
      };
    });
  }, [projects, simulation]);

  /** IDs der Projekte, deren angezeigte Werte aus der Simulation stammen */
  const simulierteIds = useMemo(
    () => new Set(Object.keys(simulation?.ergebnisse ?? {})),
    [simulation],
  );

  return {
    /** Fortschritt des laufenden Durchlaufs (für ProgressDialog) */
    fortschritt: zustand,
    abbrechen,
    simulieren,
    zuruecksetzen,
    simulation,
    angezeigteProjekte,
    simulierteIds,
  };
};
